import type { RefObject } from "react"
import { useEffect, useRef, useState } from "react"

/**
 * A custom React hook that observes and tracks the dimensions (width and height) of a DOM element.
 *
 * This hook uses the ResizeObserver API to detect changes in the element's dimensions
 * and updates the state accordingly. It optimizes performance by only updating when
 * the dimensions actually change.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const [ref, dimensions] = useResizeObserver();
 *
 *   return (
 *     <div ref={ref}>
 *       Current width: {dimensions.width}px
 *       Current height: {dimensions.height}px
 *     </div>
 *   );
 * }
 * ```
 *
 * @returns A tuple containing:
 *  - A ref object to attach to the target DOM element
 *  - An object containing the current dimensions:
 *    - width: The current width of the observed element (undefined initially)
 *    - height: The current height of the observed element (undefined initially)
 */
export const useResizeObserver = (): [RefObject<HTMLDivElement>, { width?: number; height?: number }] => {
  const [dimensions, setDimensions] = useState<{ width?: number; height?: number }>({
    width: undefined,
    height: undefined,
  })
  const observedRef = useRef<HTMLDivElement>(null)
  const sizesRef = useRef({ width: -1, height: -1 })

  useEffect(() => {
    if (!observedRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      if (observedRef.current?.offsetWidth !== sizesRef.current.width) {
        sizesRef.current.width = observedRef.current?.offsetWidth ?? 0
        setDimensions((prev) => ({ ...prev, width: observedRef.current?.offsetWidth }))
      }
      if (observedRef.current?.offsetHeight !== sizesRef.current.height) {
        sizesRef.current.height = observedRef.current?.offsetHeight ?? 0
        setDimensions((prev) => ({ ...prev, height: observedRef.current?.offsetHeight }))
      }
    })

    resizeObserver.observe(observedRef.current)

    return () => {
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observedRef.current])

  return [observedRef, dimensions]
}
